import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import {
   ChangeAudioInputDeviceId,
   ChangeAudioOutputDeviceId,
   ChangeRingerDeviceId,
} from 'src/app/store/local-storage/local-storage-settings/audio-devices-settings.actions';
import { Observable, of } from 'rxjs';
import { AudioService } from 'src/app/services/audio/audio.service';
import { AudioDevicesSettingsSelectors } from 'src/app/store/local-storage/local-storage-settings/audio-devices-settings.selectors';
import { faMicrophone, faPlay, faStop, IconDefinition } from '@fortawesome/pro-duotone-svg-icons';
import { AudioSettingsState } from '../../../../../store/settings/audio-settings/audio-settings.state';
import {
   StopAllDeviceTests,
   ToggleDeviceTestForOutputDevice,
   ToggleDeviceTestForRingingDevice,
} from '../../../../../store/settings/audio-settings/audio-settings.actions';
import { AudioDeviceTestService } from '../../../../../services/audio/audio-device-test.service';
import { map } from 'rxjs/operators';
import _ from 'lodash';

@Component({
   selector: 'samweb-audio-device-selector',
   templateUrl: './audio-device-selector.component.html',
   styleUrls: ['./audio-device-selector.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioDeviceSelectorComponent implements OnInit, OnDestroy {
   @Input()
   public deviceType: 'audiooutput' | 'audioinput' | 'ringer' = 'audiooutput';

   public isFeatureUnavailable = false;
   public playingTestSoundIcon: IconDefinition = faStop;
   public stoppedTestSoundIcon: IconDefinition = faPlay;
   public microphoneIcon: IconDefinition = faMicrophone;

   public audioDevices$: Observable<Array<MediaDeviceInfo>>;
   public selectedDeviceId$: Observable<string>;
   public isDeviceTestActive$: Observable<boolean>;
   public inputDeviceIconOpacity$: Observable<number>;

   constructor(private store: Store, private audioService: AudioService, private audioDeviceTestService: AudioDeviceTestService) {}

   ngOnInit() {
      if (this.deviceType !== 'audioinput' && !this.audioService.isSettingOutputAudioDeviceAvailable()) {
         this.isFeatureUnavailable = true;
      }
      switch (this.deviceType) {
         case 'audioinput':
            this.audioDevices$ = this.audioService.availableAudioInputDevices$;
            this.selectedDeviceId$ = this.store.select(AudioDevicesSettingsSelectors.getAudioInputDeviceId);
            this.isDeviceTestActive$ = of(true);
            break;
         case 'audiooutput':
            this.audioDevices$ = this.audioService.availableAudioOutputDevices$;
            this.selectedDeviceId$ = this.store.select(AudioDevicesSettingsSelectors.getAudioOutputDeviceId);
            this.isDeviceTestActive$ = this.store.select(AudioSettingsState.isTestActiveForOutputDevice);
            break;
         case 'ringer':
            this.audioDevices$ = this.audioService.availableRingerDevices$;
            this.selectedDeviceId$ = this.store.select(AudioDevicesSettingsSelectors.getRingerDeviceId);
            this.isDeviceTestActive$ = this.store.select(AudioSettingsState.isTestActiveForRingingDevice);
            break;
      }
      this.inputDeviceIconOpacity$ = this.audioDeviceTestService.inputDeviceVolume.pipe(map((deviceVolume) => _.max([deviceVolume, 0.1])));
   }

   ngOnDestroy() {
      this.store.dispatch(new StopAllDeviceTests());
   }

   public onSelectionChange(device: MediaDeviceInfo) {
      switch (this.deviceType) {
         case 'audioinput':
            this.store.dispatch(new ChangeAudioInputDeviceId(device.deviceId));
            break;
         case 'audiooutput':
            this.store.dispatch(new ChangeAudioOutputDeviceId(device.deviceId));
            break;
         case 'ringer':
            this.store.dispatch(new ChangeRingerDeviceId(device.deviceId));
            break;
      }
   }

   public toggleDeviceTest(): void {
      switch (this.deviceType) {
         case 'ringer':
            this.store.dispatch(new ToggleDeviceTestForRingingDevice());
            break;
         case 'audiooutput':
            this.store.dispatch(new ToggleDeviceTestForOutputDevice());
            break;
      }
   }
}
